/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useEffect } from "react"
import { useNavigate } from "react-router"

import { ApplicationSearchViewmodel } from "../../features/application-search/application-search-viewmodel"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { SessionViewModel } from "../../features/session/session-view-model"
import { useViewModel } from "../app/app-hooks"
import { ApplicationSearch } from "../ui-widgets/application-search/application-search"
import { DashboardRowView } from "./dashboard-row-view"
import { DashboardTileType, DashboardTileView } from "./dashboard-tile-view"

import "./dashboard-view.css"

export const DashboardView = observer(() => {
    const vm = useViewModel(ApplicationTypeViewModel)
    const sessionVM = useViewModel(SessionViewModel)
    const searchVM = useViewModel(ApplicationSearchViewmodel)
    const navigate = useNavigate()

    // We are using joined enabledApps because array is a reference type,
    // and we want to only recalculate aggregated threats when enabledApps values change.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the join is the point — depending on the array itself would recalculate on every new reference
    useEffect(() => {
        vm.recalculateAggregatedThreats(sessionVM.enabledApps)
    }, [sessionVM.enabledApps.join(",")])

    return (
        <div className="w-content dashboard-view">
            <div className="col-xxl-8 col-xl-10 col-md-12">
                <div className="flex flex-wrap gap-y-2 justify-between items-center mb-6">
                    <h3 className="title mb-0">Dashboard</h3>
                    <ApplicationSearch
                        onSearch={async (query: string) => {
                            const redirect = await searchVM.searchApplicationByQuery(query)
                            if (redirect) {
                                navigate(redirect)
                            }
                        }}
                    />
                </div>

                <div className="row tile-row flex justify-end mt-12 mb-12 me-1">
                    <DashboardTileView
                        type={DashboardTileType.insecure}
                        title={"Insecure"}
                        count={vm.aggregatedInsecure}
                    />
                    <DashboardTileView
                        type={DashboardTileType.outdated}
                        title={"Outdated"}
                        count={vm.aggregatedOutdated}
                    />
                    <DashboardTileView type={DashboardTileType.active} title={"Active"} count={vm.aggregatedActive} />
                </div>

                <div className="card shadow-legacy-sm overflow-hidden">
                    <div className="table-responsive">
                        <table className="table table-sm mb-0 table-extra-spacing align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="col-9">Application Name</th>
                                    <th className="col-1 text-center">Insecure</th>
                                    <th className="col-1 text-center">Outdated</th>
                                    <th className="col-1 text-center">Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vm.filterFavApplicationTypes(sessionVM.enabledApps).map((appType) => (
                                    <DashboardRowView
                                        key={appType.id ?? -1}
                                        appType={appType}
                                        threat={vm.threatOverviewFor(appType.id ?? null)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    )
})
